import React, { useEffect, useRef } from 'react'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../store/timesheetStore'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useLocalStorage } from '@uidotdev/usehooks'
import { addFilters, Filters, TimesheetStatus } from '../../store/slices/timesheet/timesheetDetailSlice'
import { calculateFiltersDates } from '../../utils/filters'
import useGetPcaUserName from '../../utils/hooks/useGetPca'

/** Número de slots de filtros persistidos (sem abas, mantemos 1 slot). */
const TAB_COUNT = 1

/** Chave por utilizador → array de filtros por aba. */
export type StoredFiltersByUser = Record<string, (Filters | null)[]>

const ORDER_BY_COLUMNS: Filters['orderByColumn'][] = ['TimeSheetDate', 'DriverName', 'DriverId', 'InternalNumber']

function isValidStoredFilters(value: unknown): value is Filters {
	if (!value || typeof value !== 'object') return false
	const f = value as Record<string, unknown>
	if (typeof f.currentPage !== 'number' || typeof f.pageSize !== 'number') return false
	if (typeof f.ascending !== 'boolean') return false
	if (!ORDER_BY_COLUMNS.includes(f.orderByColumn as Filters['orderByColumn'])) return false
	return true
}

function getFallbackFiltersForTab(): Filters {
	const startDate = String(calculateFiltersDates('StartDate'))
	const endDate = String(calculateFiltersDates('EndDate'))
	const base = {
		startDate,
		endDate,
		driverId: undefined as number | undefined,
		branchId: undefined as number | undefined,
		currentPage: 1,
		pageSize: 10,
		ascending: false,
		orderByColumn: 'TimeSheetDate' as const,
	}
	const allStatus: TimesheetStatus[] = ['Submitted', 'NotSubmitted', 'SubmittedWithoutChanges', 'AutoSubmitted', 'Rejected', 'Edited', 'Approved']
	return { ...base, status: allStatus }
}

/**
 * Converte o valor em `localStorage` para um array fixo por aba.
 * Suporta: objeto único legado → [obj, null]; array de query strings legado → slots null.
 */
function normalizeStoredFiltersByTab(raw: unknown): (Filters | null)[] {
	const empty = (): (Filters | null)[] => Array.from({ length: TAB_COUNT }, () => null)
	if (raw == null) return empty()
	if (typeof raw === 'string') return empty()
	if (isValidStoredFilters(raw)) {
		return [raw, ...Array.from({ length: TAB_COUNT - 1 }, () => null)]
	}
	if (!Array.isArray(raw)) return empty()
	const out: (Filters | null)[] = []
	for (let i = 0; i < TAB_COUNT; i++) {
		const el = raw[i]
		if (el == null || typeof el === 'string') {
			out.push(null)
			continue
		}
		out.push(isValidStoredFilters(el) ? el : null)
	}
	return out
}

function emptyTabs(): (Filters | null)[] {
	return Array.from({ length: TAB_COUNT }, () => null)
}

/** Garante um objeto raiz por utilizador; migra formatos antigos (array no topo ou um único Filters). */
function normalizeStoredRoot(raw: unknown, userKey: string): StoredFiltersByUser {
	if (raw == null) return {}
	if (typeof raw === 'string') return {}
	if (isValidStoredFilters(raw)) {
		return { [userKey]: [raw, ...Array.from({ length: TAB_COUNT - 1 }, () => null)] }
	}
	if (Array.isArray(raw)) {
		return { [userKey]: normalizeStoredFiltersByTab(raw) }
	}
	if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
		const out: StoredFiltersByUser = {}
		for (const [k, v] of Object.entries(raw)) {
			out[k] = normalizeStoredFiltersByTab(v)
		}
		return out
	}
	return {}
}

function getTabsForUser(root: StoredFiltersByUser, userKey: string): (Filters | null)[] {
	const tabs = root[userKey]
	if (tabs && tabs.length >= TAB_COUNT) return tabs
	if (tabs) {
		const padded = [...tabs]
		while (padded.length < TAB_COUNT) padded.push(null)
		return padded.slice(0, TAB_COUNT)
	}
	return emptyTabs()
}

/**
 * Mesma convenção de query string que TableMotoristasFilters (serializeFiltersToURL).
 */
function serializeFiltersToSearchParams(filters: Filters): URLSearchParams {
	const params = new URLSearchParams()
	if (filters.startDate) params.set('StartDate', filters.startDate)
	if (filters.endDate) params.set('EndDate', filters.endDate)
	if (filters.driverId && Number(filters.driverId) !== 0) params.set('DriverId', String(filters.driverId))
	if (filters.branchId && Number(filters.branchId) !== 0) params.set('BranchId', String(filters.branchId))
	if (filters.status && filters.status.length > 0) {
		filters.status.forEach(status => params.append('Status', status))
	}
	if (filters.currentPage && filters.currentPage !== 1) params.set('CurrentPage', filters.currentPage.toString())
	if (filters.ascending === false) {
		params.set('Ascending', 'false')
	} else {
		params.set('Ascending', 'true')
	}
	if (filters.orderByColumn) params.set('OrderByColumn', filters.orderByColumn)
	if (filters.pageSize && filters.pageSize !== 50) params.set('PageSize', filters.pageSize.toString())
	return params
}

/** Compara query strings de forma estável (evita loop com ordem de chaves). */
function searchParamsCanonicalString(params: URLSearchParams): string {
	const entries = Array.from(params.entries()).sort(([a, av], [b, bv]) => {
		const k = a.localeCompare(b)
		return k !== 0 ? k : String(av).localeCompare(String(bv))
	})
	return new URLSearchParams(entries).toString()
}

/**
 * Serviço de sincronização dos filtros da tabela de motoristas entre três destinos:
 *
 * - **Redux** (`timesheetDetails.filters`): fonte usada pelos hooks/APIs para montar o pedido ao endpoint.
 * - **localStorage** (`timesheetTableFilters`): objeto **por utilizador** (`userName` / conta MSAL), cada valor é um array
 *   com um slot por aba — assim cada utilizador mantém as suas preferências por aba.
 * - **Search params da URL**: reflete o estado da aba ativa na barra de endereços.
 *
 * Ao mudar de aba ou de utilizador, hidrata o Redux a partir do par (utilizador, aba) e mantém URL + LS alinhados com
 * guardas para evitar ciclos de atualização.
 */
function TableMotoristasFiltersService(): React.JSX.Element {
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const timesheetFilters = useSelector((state: TimesheetStoreState) => state.timesheetDetails.filters)
	const timesheetTabs = useSelector((state: TimesheetStoreState) => state.timesheetTabs)
	const tabIndex = Math.min(Math.max(timesheetTabs.tab, 0), TAB_COUNT - 1)

	const [storedRoot, setStoredRoot] = useLocalStorage<StoredFiltersByUser | null | string>(
		'timesheetTableFilters',
		null,
	)
	const [, setSearchParams] = useSearchParams()

	const userNameFromAccount = useGetPcaUserName()

	const userKey = userNameFromAccount ?? '_guest'

	const lastContextRef = useRef<string | null>(null)
	const lastSyncedJsonRef = useRef<string | null>(null)

	const contextKey = `${userKey}:${tabIndex}`

	useEffect(() => {

		if (typeof storedRoot === 'string') {
			setStoredRoot({})
		}

		const root = normalizeStoredRoot(storedRoot, userKey)
		const arr = getTabsForUser(root, userKey)

		// Montagem, troca de aba ou troca de utilizador: hidratar Redux a partir do slot
		if (lastContextRef.current !== contextKey) {
			lastContextRef.current = contextKey
			lastSyncedJsonRef.current = null
			const forTab = arr[tabIndex] ?? null
			if (isValidStoredFilters(forTab)) {
				dispatch(addFilters(forTab))
			} else {
				dispatch(addFilters(getFallbackFiltersForTab()))
			}
			return
		}

		const json = JSON.stringify(timesheetFilters)
		if (lastSyncedJsonRef.current === json) {
			return
		}
		lastSyncedJsonRef.current = json

		const nextArr = [...arr]
		while (nextArr.length < TAB_COUNT) nextArr.push(null)
		nextArr[tabIndex] = timesheetFilters
		setStoredRoot({ ...root, [userKey]: nextArr })

		const next = serializeFiltersToSearchParams(timesheetFilters)
		let current: URLSearchParams
		try {
			current = new URLSearchParams(window.location.search)
		} catch {
			current = new URLSearchParams()
		}
		if (searchParamsCanonicalString(next) !== searchParamsCanonicalString(current)) {
			setSearchParams(next, { replace: true })
		}
	}, [timesheetFilters, storedRoot, tabIndex, userKey, contextKey, dispatch, setStoredRoot, setSearchParams])

	return <></>
}

export default TableMotoristasFiltersService
